import { ExternalLink } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { Button } from '../../shared/ui/Button';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';

import s from './DocumentReviewActions.module.css';

interface Props {
  /** Names the file in every accessible name — "Accept Passport scan". */
  label: string;
  storagePath: string | null;
  externalUrl: string | null;
  busy: boolean;
  /** Mint a signed URL and open it. Not called for a link — that is a plain `<a>`. */
  onOpen: () => void;
  onAccept: () => void;
  /** The reason is required; the student reads it word for word. */
  onReject: (reason: string) => void;
}

/**
 * Open · Accept · Send back — the three things anyone does with a file a
 * student sent (phase 174).
 *
 * One component for the work queue, the card drawer and the dossier, so the
 * review is the same gesture everywhere it appears. "Send back" expands a
 * reason field in place rather than opening a dialog: the row is the context,
 * and a dialog over a drawer over a board is three layers of chrome for one
 * sentence.
 */
export function DocumentReviewActions({
  label,
  storagePath,
  externalUrl,
  busy,
  onOpen,
  onAccept,
  onReject,
}: Props): JSX.Element {
  const fid = useId();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState(false);
  const reasonRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (rejecting) reasonRef.current?.focus();
  }, [rejecting]);

  const hasLink = externalUrl !== null && externalUrl !== '';
  const hasFile = storagePath !== null && storagePath !== '';

  return (
    <div className={s.root}>
      <div className={s.row}>
        {hasLink ? (
          <a
            className={s.link}
            href={externalUrl ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open the link for ${label}`}
          >
            <ExternalLink size={13} aria-hidden="true" />
            Open link
          </a>
        ) : hasFile ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            aria-label={`Open ${label}`}
            onClick={onOpen}
          >
            Open
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          loading={busy}
          aria-label={`Accept ${label}`}
          onClick={onAccept}
        >
          Accept
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy}
          aria-expanded={rejecting}
          aria-label={`Send back ${label}`}
          onClick={() => {
            setRejecting((value) => !value);
          }}
        >
          Send back
        </Button>
      </div>

      {rejecting ? (
        <form
          className={s.reason}
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = reason.trim();
            if (trimmed === '') {
              setMissing(true);
              reasonRef.current?.focus();
              return;
            }
            onReject(trimmed);
            setRejecting(false);
            setReason('');
          }}
        >
          <Label htmlFor={`${fid}-why`}>What needs to change</Label>
          <Input
            id={`${fid}-why`}
            ref={reasonRef}
            value={reason}
            maxLength={500}
            placeholder="The photo is blurry — please rescan every page"
            aria-invalid={missing}
            aria-describedby={missing ? `${fid}-err` : undefined}
            onChange={(event) => {
              setReason(event.target.value);
              if (missing) setMissing(false);
            }}
          />
          {missing ? (
            <p id={`${fid}-err`} className={s.error}>
              Say what to fix — the student reads this line.
            </p>
          ) : (
            <p className={s.hint}>The student sees this and can upload again.</p>
          )}
          <div className={s.reasonActions}>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setRejecting(false);
                setReason('');
                setMissing(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" variant="danger" loading={busy}>
              Send it back
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
